import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  createAppointmentType,
  deleteAppointmentType,
  getAdminAppointmentTypes,
  updateAppointmentType,
} from '../lib/api';
import {
  DAYS_OF_WEEK,
  emptyTypeSchedule,
  formatTypeExceptionScope,
  normalizeTypeScheduleFromApi,
} from '../lib/appointmentTypeSchedule';
import { datePickerToClinicYmd, formatAppointmentDateOnlyInClinic, ymdToDatePickerDate } from '../lib/clinicTime';
import { Calendar } from '../components/ui/calendar';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';
import { Building2, CalendarDays, Clock, Plus, Save, Trash2, Pencil } from 'lucide-react';

function formatSaveError(err) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0];
    const path = (first?.loc || []).filter((x) => x !== 'body').join('.');
    if (path.includes('label')) {
      return 'El nombre del servicio debe tener al menos 2 caracteres.';
    }
    if (path.includes('exceptions') && path.includes('date')) {
      return 'Hay una excepción con fecha inválida. Revísala e inténtalo de nuevo.';
    }
    const msg = first?.msg || 'Datos inválidos';
    return path ? `${path}: ${msg}` : msg;
  }
  return 'Error al guardar';
}

function schedulePayloadForApi(schedule) {
  const exceptions = (schedule.exceptions || []).map((e) => {
    const partial = e.all_day === false || Boolean(String(e.closed_start || '').trim());
    return {
      id: e.id,
      date: String(e.date || '').trim().slice(0, 10),
      reason: String(e.reason || '').trim(),
      is_closed: e.is_closed !== false,
      all_day: !partial,
      closed_start: partial ? String(e.closed_start || '').slice(0, 5) || null : null,
      closed_end: partial ? String(e.closed_end || '').slice(0, 5) || null : null,
    };
  });
  const per_day = {};
  for (let d = 1; d <= 7; d++) {
    const k = String(d);
    const day = schedule.per_day?.[k] || {};
    per_day[k] = {
      opening_time: day.opening_time || schedule.opening_time || '09:00',
      closing_time: day.closing_time || schedule.closing_time || '17:00',
      lunch_start: day.lunch_start || schedule.lunch_start || '14:00',
      lunch_end: day.lunch_end || schedule.lunch_end || '15:00',
      has_lunch_break: day.has_lunch_break !== false,
    };
  }
  const { has_lunch_break: _legacy, ...rest } = schedule;
  return { ...rest, per_day, exceptions };
}

function newExceptionDraft() {
  return {
    date: null,
    reason: '',
    allDay: true,
    closedStart: '14:00',
    closedEnd: '17:00',
  };
}

function TypeExceptionsEditor({ exceptions, onChange }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draft, setDraft] = useState(newExceptionDraft);

  const handleAdd = () => {
    if (!draft.date || !draft.reason.trim()) {
      toast.error('Completa fecha y motivo');
      return;
    }
    if (!draft.allDay) {
      if (!draft.closedStart || !draft.closedEnd) {
        toast.error('Indica hora de inicio y fin del cierre');
        return;
      }
      if (draft.closedStart >= draft.closedEnd) {
        toast.error('La hora de fin debe ser posterior a la de inicio');
        return;
      }
    }
    const dateYmd = datePickerToClinicYmd(draft.date);
    const entry = {
      id: `exc-${Date.now()}`,
      date: dateYmd,
      reason: draft.reason.trim(),
      is_closed: true,
      all_day: draft.allDay,
      closed_start: draft.allDay ? null : draft.closedStart,
      closed_end: draft.allDay ? null : draft.closedEnd,
    };
    const sorted = [...exceptions, entry].sort((a, b) => String(a.date).localeCompare(String(b.date)));
    onChange(sorted);
    setDialogOpen(false);
    setDraft(newExceptionDraft());
    toast.success('Excepción añadida al servicio');
  };

  const handleRemove = (id) => {
    if (!window.confirm('¿Eliminar esta excepción del servicio?')) return;
    onChange(exceptions.filter((e) => e.id !== id));
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          Cierres puntuales solo para este servicio (festivos, cursos, etc.). Las excepciones globales de Admin → Cierres
          también aplican.
        </p>
        <Button type="button" size="sm" variant="outline" className="shrink-0 gap-1" onClick={() => setDialogOpen(true)}>
          <Plus className="w-4 h-4" />
          Agregar
        </Button>
      </div>
      {exceptions.length === 0 ? (
        <div className="rounded-lg border border-dashed py-8 text-center text-sm text-muted-foreground">
          <CalendarDays className="w-10 h-10 mx-auto mb-2 opacity-40" />
          Sin excepciones para este servicio
        </div>
      ) : (
        <div className="rounded-lg border overflow-hidden max-h-56 overflow-y-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Fecha</TableHead>
                <TableHead>Alcance</TableHead>
                <TableHead>Motivo</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {exceptions.map((exc) => (
                <TableRow key={exc.id}>
                  <TableCell className="whitespace-nowrap text-sm">
                    {formatAppointmentDateOnlyInClinic(exc.date, 'd MMM yyyy')}
                  </TableCell>
                  <TableCell className="text-sm">{formatTypeExceptionScope(exc)}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{exc.reason}</TableCell>
                  <TableCell>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="text-destructive h-8 w-8"
                      onClick={() => handleRemove(exc.id)}
                      aria-label="Eliminar excepción"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Excepción de horario del servicio</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Fecha</Label>
              <Calendar
                mode="single"
                selected={draft.date}
                onSelect={(date) => {
                  if (!date) {
                    setDraft((prev) => ({ ...prev, date: null }));
                    return;
                  }
                  const ymd = datePickerToClinicYmd(date);
                  setDraft((prev) => ({ ...prev, date: ymdToDatePickerDate(ymd) }));
                }}
                fromDate={new Date()}
                locale={es}
                className="rounded-lg border mt-2"
              />
            </div>
            <div>
              <Label htmlFor="type-exc-reason">Motivo</Label>
              <Input
                id="type-exc-reason"
                value={draft.reason}
                onChange={(e) => setDraft((prev) => ({ ...prev, reason: e.target.value }))}
                placeholder="Ej. Día festivo, curso, mantenimiento"
                className="mt-1"
              />
            </div>
            <label className="flex items-start gap-3 cursor-pointer rounded-lg border p-3">
              <Checkbox
                checked={draft.allDay}
                onCheckedChange={(c) => setDraft((prev) => ({ ...prev, allDay: Boolean(c) }))}
              />
              <span className="text-sm">
                <span className="font-medium">Cerrar todo el día</span>
                <span className="block text-muted-foreground text-xs mt-0.5">
                  Sin cupos de este servicio en ningún horario
                </span>
              </span>
            </label>
            {!draft.allDay ? (
              <div className="grid grid-cols-2 gap-3 rounded-lg border border-dashed p-3">
                <div>
                  <Label>Desde (sin servicio)</Label>
                  <Input
                    type="time"
                    className="mt-1"
                    value={draft.closedStart}
                    onChange={(e) => setDraft((prev) => ({ ...prev, closedStart: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Hasta (sin servicio)</Label>
                  <Input
                    type="time"
                    className="mt-1"
                    value={draft.closedEnd}
                    onChange={(e) => setDraft((prev) => ({ ...prev, closedEnd: e.target.value }))}
                  />
                </div>
              </div>
            ) : null}
            <Button type="button" className="w-full" onClick={handleAdd}>
              Añadir excepción
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TypeScheduleEditor({ schedule, onChange }) {
  const handleDayToggle = (dayId) => {
    const days = schedule.working_days.includes(dayId)
      ? schedule.working_days.filter((d) => d !== dayId)
      : [...schedule.working_days, dayId].sort((a, b) => a - b);
    onChange({ ...schedule, working_days: days });
  };

  const handlePerDay = (dayId, field, value) => {
    const k = String(dayId);
    onChange({
      ...schedule,
      per_day: {
        ...schedule.per_day,
        [k]: { ...schedule.per_day[k], [field]: value },
      },
    });
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Duración de cita (minutos)</Label>
        <Input
          type="number"
          min={5}
          max={240}
          value={schedule.appointment_duration}
          onChange={(e) => onChange({ ...schedule, appointment_duration: Number(e.target.value) || 30 })}
        />
      </div>
      <div className="flex flex-wrap gap-3">
        {DAYS_OF_WEEK.map((d) => (
          <label key={d.id} className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={schedule.working_days.includes(d.id)}
              onCheckedChange={() => handleDayToggle(d.id)}
            />
            {d.name}
          </label>
        ))}
      </div>
      <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
        {DAYS_OF_WEEK.map((d) => {
          const day = schedule.per_day[String(d.id)] || {};
          const hasLunch = day.has_lunch_break !== false;
          const dayTimeFields = hasLunch
            ? [
                { key: 'opening_time', label: 'Apertura' },
                { key: 'closing_time', label: 'Cierre' },
                { key: 'lunch_start', label: 'Inicio comida' },
                { key: 'lunch_end', label: 'Fin comida' },
              ]
            : [
                { key: 'opening_time', label: 'Apertura' },
                { key: 'closing_time', label: 'Cierre' },
              ];
          return (
            <div
              key={d.id}
              className={`grid grid-cols-2 ${hasLunch ? 'sm:grid-cols-4' : 'sm:grid-cols-2'} gap-2 p-3 border border-border rounded-lg text-sm`}
            >
              <div className="col-span-2 sm:col-span-full flex flex-wrap items-center justify-between gap-2">
                <span className="font-medium">{d.name}</span>
                <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                  <Checkbox
                    checked={hasLunch}
                    onCheckedChange={(c) => handlePerDay(d.id, 'has_lunch_break', Boolean(c))}
                  />
                  Tiene hora de comida
                </label>
              </div>
              {dayTimeFields.map(({ key, label }) => (
                <div key={key} className="space-y-1">
                  <Label className="text-xs">{label}</Label>
                  <Input
                    type="time"
                    value={day[key] || '09:00'}
                    onChange={(e) => handlePerDay(d.id, key, e.target.value)}
                  />
                </div>
              ))}
              {!hasLunch ? (
                <p className="col-span-2 sm:col-span-full text-xs text-muted-foreground">
                  Horario corrido de apertura a cierre.
                </p>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function AdminAppointmentTypes() {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    label: '',
    room_count: 1,
    room_label: 'Consultorio',
    enabled: true,
    sort_order: 0,
  });
  const [schedule, setSchedule] = useState(emptyTypeSchedule());
  const [saving, setSaving] = useState(false);

  const setTypeExceptions = (exceptions) => {
    setSchedule((prev) => ({ ...prev, exceptions }));
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAdminAppointmentTypes();
      setTypes(Array.isArray(data) ? data : []);
    } catch {
      toast.error('No se pudieron cargar los tipos de cita');
      setTypes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({
      label: '',
      room_count: 1,
      room_label: 'Consultorio',
      enabled: true,
      sort_order: types.length + 1,
    });
    setSchedule(emptyTypeSchedule());
    setDialogOpen(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({
      label: t.label,
      room_count: t.room_count,
      room_label: t.room_label || 'Consultorio',
      enabled: t.enabled,
      sort_order: t.sort_order ?? 0,
    });
    setSchedule(normalizeTypeScheduleFromApi(t.schedule));
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const label = form.label.trim();
    if (!label) {
      toast.error('Indica el nombre del servicio');
      return;
    }
    if (label.length < 2) {
      toast.error('El nombre del servicio debe tener al menos 2 caracteres');
      return;
    }
    for (const exc of schedule.exceptions || []) {
      if (!String(exc.date || '').trim()) {
        toast.error('Hay una excepción sin fecha válida');
        return;
      }
      const partial = exc.all_day === false || Boolean(String(exc.closed_start || '').trim());
      if (partial && (!exc.closed_start || !exc.closed_end)) {
        toast.error('En excepciones parciales indica hora de inicio y fin');
        return;
      }
      if (partial && exc.closed_start >= exc.closed_end) {
        toast.error('En excepciones parciales la hora de fin debe ser posterior al inicio');
        return;
      }
    }
    const roomCount = Number(form.room_count) || 1;
    const roomLabel = String(form.room_label || '').trim();
    if (!roomLabel) {
      toast.error('Indica el tipo de estación de trabajo (Silla, Consultorio o Salón)');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        label,
        room_count: roomCount,
        room_label: roomLabel,
        enabled: form.enabled,
        sort_order: Number(form.sort_order) || 0,
        schedule: schedulePayloadForApi(schedule),
      };
      if (editing) {
        await updateAppointmentType(editing.id, payload);
        toast.success('Tipo actualizado');
      } else {
        await createAppointmentType(payload);
        toast.success('Tipo de servicio creado');
      }
      setDialogOpen(false);
      await load();
    } catch (err) {
      toast.error(formatSaveError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (t) => {
    try {
      await updateAppointmentType(t.id, { enabled: !t.enabled });
      toast.success(t.enabled ? 'Tipo desactivado' : 'Tipo activado');
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Error al cambiar estado');
    }
  };

  const handleDelete = async (t) => {
    if (t.is_system) return;
    if (!window.confirm(`¿Eliminar «${t.label}»?`)) return;
    try {
      await deleteAppointmentType(t.id);
      toast.success('Tipo eliminado');
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'No se pudo eliminar');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-muted-foreground">Cargando tipos de cita…</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-appointment-types">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground">Tipos de cita</h1>
          <p className="text-muted-foreground">
            Alta de servicios (Fisioterapia, DermoTerapia, etc.), consultorios por tipo y horarios independientes.
            Los tipos activos aparecen en web, WhatsApp y panel de citas. Los tipos legacy del sistema se pueden
            activar o desactivar desde esta lista.
          </p>
        </div>
        <Button type="button" className="gap-2" onClick={openCreate}>
          <Plus className="w-4 h-4" />
          Añadir tipo de servicio
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-primary" />
            Catálogo de servicios
          </CardTitle>
          <CardDescription>
            Cada tipo define cuántos consultorios hay (cupos simultáneos por hora) y su propio horario.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Servicio</TableHead>
                <TableHead>Lugares</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Orden</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {types.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-10 text-muted-foreground">
                    No hay tipos configurados. Crea el primero con el botón superior.
                  </TableCell>
                </TableRow>
              ) : (
                types.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{t.label}</span>
                        {t.is_system ? (
                          <Badge variant="outline" className="text-xs">
                            {t.legacy_kind ? 'Legacy' : 'Sistema'}
                          </Badge>
                        ) : null}
                      </div>
                      <p className="text-xs text-muted-foreground">{t.id}</p>
                    </TableCell>
                    <TableCell>
                      {t.room_count} · {t.room_label || 'Consultorio'}
                    </TableCell>
                    <TableCell>
                      <Badge variant={t.enabled ? 'default' : 'secondary'}>
                        {t.enabled ? 'Activo' : 'Inactivo'}
                      </Badge>
                    </TableCell>
                    <TableCell>{t.sort_order}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button type="button" variant="ghost" size="icon" onClick={() => openEdit(t)} aria-label="Editar">
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggle(t)}
                        >
                          {t.enabled ? 'Desactivar' : 'Activar'}
                        </Button>
                        {!t.is_system ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="text-destructive"
                            onClick={() => handleDelete(t)}
                            aria-label="Eliminar"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        ) : null}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-5xl w-[min(100vw-2rem,64rem)] max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? `Editar: ${editing.label}` : 'Nuevo tipo de servicio'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Nombre del servicio</Label>
                <Input
                  value={form.label}
                  onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                  placeholder="Ej. Fisioterapia"
                  minLength={2}
                />
              </div>
              <div className="space-y-2">
                <Label>Tipo de estación de trabajo *</Label>
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                  value={
                    ['Silla', 'Consultorio', 'Salón'].includes(String(form.room_label || '').trim())
                      ? String(form.room_label).trim()
                      : '__custom__'
                  }
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '__custom__') {
                      setForm((f) => ({
                        ...f,
                        room_label: ['Silla', 'Consultorio', 'Salón'].includes(String(f.room_label || '').trim())
                          ? ''
                          : f.room_label,
                      }));
                      return;
                    }
                    setForm((f) => ({ ...f, room_label: v }));
                  }}
                >
                  <option value="Silla">Silla</option>
                  <option value="Consultorio">Consultorio</option>
                  <option value="Salón">Salón</option>
                  <option value="__custom__">Otro…</option>
                </select>
                {!['Silla', 'Consultorio', 'Salón'].includes(String(form.room_label || '').trim()) ? (
                  <Input
                    value={form.room_label}
                    onChange={(e) => setForm((f) => ({ ...f, room_label: e.target.value }))}
                    placeholder="Ej. Cabina, Cancha"
                    maxLength={40}
                  />
                ) : null}
                <p className="text-xs text-muted-foreground">
                  En el calendario se muestra como «{String(form.room_label || 'Silla').trim() || 'Silla'} 1», «
                  {String(form.room_label || 'Silla').trim() || 'Silla'} 2», etc. según la cantidad.
                </p>
              </div>
              <div className="space-y-2">
                <Label>
                  Nº de{' '}
                  {String(form.room_label || '').trim()
                    ? String(form.room_label).trim()
                    : 'estaciones'}{' '}
                  (cupos por hora)
                </Label>
                <Input
                  type="number"
                  min={1}
                  max={50}
                  value={form.room_count}
                  onChange={(e) => setForm((f) => ({ ...f, room_count: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Orden en menús</Label>
                <Input
                  type="number"
                  value={form.sort_order}
                  onChange={(e) => setForm((f) => ({ ...f, sort_order: e.target.value }))}
                />
              </div>
              <div className="flex items-center gap-2 pt-8">
                <Checkbox
                  id="type-enabled"
                  checked={form.enabled}
                  onCheckedChange={(v) => setForm((f) => ({ ...f, enabled: Boolean(v) }))}
                />
                <Label htmlFor="type-enabled">Activo (visible en web y WhatsApp)</Label>
              </div>
            </div>
            <div className="border-t border-border pt-4 grid lg:grid-cols-2 gap-6">
              <div>
                <h3 className="font-medium flex items-center gap-2 mb-3">
                  <Clock className="w-4 h-4" />
                  Horario de este servicio
                </h3>
                <TypeScheduleEditor schedule={schedule} onChange={setSchedule} />
              </div>
              <div>
                <h3 className="font-medium flex items-center gap-2 mb-3">
                  <CalendarDays className="w-4 h-4" />
                  Excepciones de este servicio
                </h3>
                <TypeExceptionsEditor
                  exceptions={schedule.exceptions || []}
                  onChange={setTypeExceptions}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button type="button" className="gap-2" onClick={handleSave} disabled={saving}>
              <Save className="w-4 h-4" />
              {saving ? 'Guardando…' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
